// Client-side end-to-end encryption for Sync & Backup.
// Zero-knowledge: the passphrase never leaves the browser; the server (if it ever retains
// a backup) only ever sees the ciphertext envelope below. AES-256-GCM for confidentiality +
// integrity; Argon2id (RFC 9106) for passphrase -> key derivation.

import { argon2id } from "hash-wasm";

// RFC 9106 "second recommended option" (memory-constrained), sized for browsers:
// m = 64 MiB, t = 3, p = 4, 256-bit key. Stored in the envelope so any device can re-derive.
const KDF = { alg: "argon2id", version: 19, t: 3, m: 65536, p: 4, keyLen: 32 };
const ENVELOPE_VERSION = 1;

const enc = new TextEncoder();
const dec = new TextDecoder();

function toB64(bytes) {
  let s = "";
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  for (let i = 0; i < arr.length; i++) s += String.fromCharCode(arr[i]);
  return btoa(s);
}
function fromB64(b64) {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

async function deriveKey(passphrase, salt) {
  const raw = await argon2id({
    password: passphrase,
    salt,
    parallelism: KDF.p,
    iterations: KDF.t,
    memorySize: KDF.m,
    hashLength: KDF.keyLen,
    outputType: "binary",
  });
  return crypto.subtle.importKey("raw", raw, { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
}

// Returns a JSON-serializable envelope. `hints` is small, NON-sensitive metadata kept in
// cleartext so the restore screen can preview the file before the passphrase is entered.
export async function encryptBackup(backupObj, passphrase, hints = {}) {
  if (!passphrase || passphrase.length < 8) throw new Error("Passphrase must be at least 8 characters.");
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(passphrase, salt);
  const plaintext = enc.encode(JSON.stringify(backupObj));
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, plaintext);
  return {
    fopfo_e2e: ENVELOPE_VERSION,
    alg: "AES-256-GCM",
    kdf: { alg: KDF.alg, v: KDF.version, t: KDF.t, m: KDF.m, p: KDF.p, salt: toB64(salt) },
    iv: toB64(iv),
    ct: toB64(new Uint8Array(ct)),
    hints: { app: hints.app || null, count: hints.count ?? null, exported_at: hints.exported_at || null, hint: hints.hint || null },
  };
}

// Lightweight passphrase strength estimator (0-4). Not a security guarantee — guidance only.
export function scorePassphrase(pass) {
  const p = pass || "";
  if (!p) return { score: 0, label: "", pct: 0 };
  let s = 0;
  if (p.length >= 8) s++;
  if (p.length >= 14) s++;
  if (/[a-z]/.test(p) && /[A-Z]/.test(p)) s++;
  if (/\d/.test(p)) s++;
  if (/[^A-Za-z0-9]/.test(p)) s++;
  if (p.length >= 20 && /\s/.test(p)) s++; // long passphrase w/ spaces
  const score = Math.min(4, Math.max(1, s - 1));
  const labels = ["", "Weak", "Fair", "Good", "Strong"];
  return { score, label: labels[score], pct: (score / 4) * 100 };
}

export async function decryptBackup(envelope, passphrase) {
  if (!isEncryptedBackup(envelope)) throw new Error("This file is not an encrypted FopFo backup.");
  const salt = fromB64(envelope.kdf.salt);
  const iv = fromB64(envelope.iv);
  const key = await deriveKey(passphrase, salt);
  let plain;
  try {
    plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, fromB64(envelope.ct));
  } catch {
    throw new Error("Wrong passphrase, or the file has been altered.");
  }
  return JSON.parse(dec.decode(plain));
}

export function isEncryptedBackup(obj) {
  return !!(obj && typeof obj === "object" && obj.fopfo_e2e && obj.kdf && obj.ct && obj.iv);
}
